const SCROLL_KEY_PREFIX = 'plp-scroll-';

/**
 * Product cards only — skip Depict beacons so re-appending pages does not
 * re-init the grid (which flashes existing cards).
 *
 * @param {Element} gridEl
 * @returns {Element[]}
 */
function getProductCards(gridEl) {
  return Array.from(
    gridEl.querySelectorAll(
      ':scope > [ref="cards[]"], :scope > .product-grid__item[data-product-id], :scope > li[data-product-id]'
    )
  );
}

/**
 * @param {Element} productGrid
 * @param {Element} newGrid
 */
function appendProductCards(productGrid, newGrid) {
  const cards = getProductCards(newGrid);
  if (!cards.length) return;

  const frag = document.createDocumentFragment();
  for (const card of cards) frag.appendChild(card);

  const endBeacon = productGrid.querySelector(':scope > depict-grid-end-beacon');
  if (endBeacon) {
    productGrid.insertBefore(frag, endBeacon);
  } else {
    productGrid.appendChild(frag);
  }
}

/**
 * @param {() => void} callback
 */
function scheduleIdle(callback) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(callback, { timeout: 1500 });
    return;
  }

  setTimeout(callback, 200);
}

window.addEventListener('beforeunload', () => {
  sessionStorage.setItem(SCROLL_KEY_PREFIX + window.location.pathname, window.scrollY.toString());
});

const grid = document.querySelector('[data-product-grid]');
const hashMatch = window.location.hash.match(/^#page=(\d+)$/);
const hashPage = hashMatch ? parseInt(hashMatch[1], 10) : 1;

if (hashPage > 1 && grid) {
  const container = document.getElementById('ProductGridContainer');
  if (container) container.style.opacity = '0';

  const sectionId = grid.getAttribute('data-id');
  let lastPageDoc = null;

  for (let page = 2; page <= hashPage; page++) {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('page', page.toString());
    if (sectionId) url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString());
      const text = await response.text();
      lastPageDoc = new DOMParser().parseFromString(text, 'text/html');
      const newGrid = lastPageDoc.querySelector('[data-product-grid]');

      if (newGrid) {
        appendProductCards(grid, newGrid);
      }
    } catch (err) {
      console.error('Failed to load page', page, err);
    }
  }

  if (lastPageDoc) {
    const currentInfinite = document.querySelector('infinite-scroll');
    const newInfinite = lastPageDoc.querySelector('infinite-scroll');
    if (currentInfinite && newInfinite) {
      currentInfinite.replaceWith(newInfinite);
    } else if (currentInfinite) {
      currentInfinite.remove();
    }
  }

  const savedScroll = sessionStorage.getItem(SCROLL_KEY_PREFIX + window.location.pathname);
  if (savedScroll) {
    window.scrollTo(0, parseInt(savedScroll, 10));
    sessionStorage.removeItem(SCROLL_KEY_PREFIX + window.location.pathname);
  }

  if (container) container.style.opacity = '1';
}

class InfiniteScroll extends HTMLElement {
  #observer = null;
  #loading = false;

  /** @type {Map<string, Promise<Document>>} */
  #pageCache = new Map();

  #onClick = (event) => {
    event.preventDefault();
    this.loadNextPage();
  };

  connectedCallback() {
    this.anchor = this.querySelector('a[href]');
    if (!this.anchor) return;

    const nextUrl = this.anchor.getAttribute('href');
    if (nextUrl) {
      scheduleIdle(() => {
        if (this.isConnected) this.#prefetch(nextUrl);
      });
    }

    if (this.hasAttribute('data-auto-load')) {
      // Wait a frame so Depict can finish first paint before we observe/load.
      requestAnimationFrame(() => {
        if (!this.isConnected || this.#loading) return;
        this.#observe();
      });
      return;
    }

    this.anchor.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.anchor?.removeEventListener('click', this.#onClick);
  }

  /**
   * @param {string | null} url
   */
  #prefetch(url) {
    if (!url || this.#pageCache.has(url)) return;

    const promise = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to fetch page: ${response.status}`);
        return response.text();
      })
      .then((text) => new DOMParser().parseFromString(text, 'text/html'))
      .catch((error) => {
        this.#pageCache.delete(url);
        throw error;
      });

    this.#pageCache.set(url, promise);
  }

  /**
   * @param {string} url
   * @returns {Promise<Document>}
   */
  async #getPageDocument(url) {
    if (!this.#pageCache.has(url)) {
      this.#prefetch(url);
    }

    try {
      return await /** @type {Promise<Document>} */ (this.#pageCache.get(url));
    } catch {
      this.#pageCache.delete(url);
      this.#prefetch(url);
      return /** @type {Promise<Document>} */ (this.#pageCache.get(url));
    }
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    this.#loading = isLoading;
    this.toggleAttribute('data-loading', isLoading);
    this.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    if (!this.anchor) return;

    this.anchor.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    if (isLoading) {
      this.anchor.setAttribute('tabindex', '-1');
    } else {
      this.anchor.removeAttribute('tabindex');
    }

    const label = this.anchor.querySelector('.infinite-scroll__label');
    const busy = this.anchor.querySelector('.infinite-scroll__busy');
    if (label instanceof HTMLElement) label.hidden = isLoading;
    if (busy instanceof HTMLElement) busy.hidden = !isLoading;
  }

  #observe() {
    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (this.#loading) return;
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadNextPage();
        }
      },
      { rootMargin: '400px 0px' }
    );

    const sentinel = this.querySelector('.infinite-scroll__sentinel');
    this.#observer.observe(sentinel || this);
  }

  async loadNextPage() {
    if (this.#loading || !this.anchor) return;

    const url = this.anchor.getAttribute('href');
    if (!url) return;

    this.#setLoading(true);
    this.#observer?.disconnect();

    try {
      const html = await this.#getPageDocument(url);

      const newGrid = html.querySelector('[data-product-grid]');
      const productGrid = document.querySelector('[data-product-grid]');

      if (newGrid && productGrid) {
        appendProductCards(productGrid, newGrid);
      }

      const fetchedUrl = new URL(url, window.location.origin);
      const page = fetchedUrl.searchParams.get('page');
      if (page) {
        history.replaceState(null, '', '#page=' + page);
      }

      const newInfinite = html.querySelector('infinite-scroll');
      if (newInfinite) {
        this.replaceWith(newInfinite);
      } else {
        this.remove();
      }
    } catch (err) {
      console.error('InfiniteScroll error:', err);
      this.#setLoading(false);
      if (this.hasAttribute('data-auto-load') && this.isConnected) {
        this.#observe();
      }
    }
  }
}

customElements.define('infinite-scroll', InfiniteScroll);
